/**
 * Page-wide per-product records (selection, uploads in flight, schedule
 * statuses) cut per category for the list's cards (plan §5.8). A card whose
 * part did not change gets the very same object back, so Vue 2 does not
 * re-render it: one tap re-renders one card, not all of them.
 */

export type TSlices<V> = Record<number, Readonly<Record<number, V>>>;

/** The slice of a category with nothing in it (shared, never recreated). */
export const EMPTY_SLICE: Readonly<Record<number, never>> = Object.freeze({});

function sameSlice<V>(
  a: Readonly<Record<number, V>>,
  b: Readonly<Record<number, V>>
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => {
    const id = Number(key);
    return id in b && a[id] === b[id];
  });
}

/**
 * `values` by product id, grouped by `categoryOf(id)` (null = unknown,
 * dropped); `previous` slices are reused when their content is the same.
 */
export function sliceByCategory<V>(
  values: Readonly<Record<number, V>>,
  categoryOf: (itemId: number) => number | null,
  previous: TSlices<V>
): TSlices<V> {
  const grouped: Record<number, Record<number, V>> = {};
  for (const key of Object.keys(values)) {
    const id = Number(key);
    const categoryId = categoryOf(id);
    if (categoryId === null) continue;
    (grouped[categoryId] = grouped[categoryId] ?? {})[id] = values[id];
  }
  const slices: TSlices<V> = {};
  for (const key of Object.keys(grouped)) {
    const categoryId = Number(key);
    const before = previous[categoryId];
    slices[categoryId] =
      before && sameSlice(before, grouped[categoryId])
        ? before
        : Object.freeze(grouped[categoryId]);
  }
  return slices;
}
